import { useEffect, useState } from "react";
import { Bell, BellOff, Share } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";

const urlB64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from([...atob(base64)].map((c) => c.charCodeAt(0)));
};

export default function ClientPushSettings({ token }) {
  const [config, setConfig] = useState(null);
  const [showPinHelp, setShowPinHelp] = useState(false);
  const [busy, setBusy] = useState(false);
  const supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  const load = () => api.get(`/review/${token}/push-config`).then((r) => setConfig(r.data)).catch(() => {});
  useEffect(load, [token]);

  const subscribe = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Notifications refusées par le navigateur"); return; }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(config.vapid_key),
      });
      await api.post(`/review/${token}/push-subscribe`, sub.toJSON());
      toast.success("Notifications activées — vous serez prévenu(e) à chaque nouveauté");
      load();
    } catch {
      toast.error(isIOS
        ? "Sur iPhone : épinglez d'abord la page sur l'écran d'accueil, puis réactivez les notifications depuis l'icône"
        : "Activation impossible sur cet appareil");
    } finally { setBusy(false); }
  };

  const setPref = async (patch) => {
    await api.patch(`/review/${token}/push-prefs`, patch).catch(() => {});
    load();
  };

  const unsubscribe = async () => {
    await api.post(`/review/${token}/push-unsubscribe`).catch(() => {});
    toast.success("Notifications désactivées");
    load();
  };

  if (!config) return null;

  return (
    <div className="flex flex-col gap-2" data-testid="client-push-settings">
      <div className="flex items-center gap-2 flex-wrap">
        {supported && !config.subscribed && (
          <Button size="sm" variant="outline" onClick={subscribe} disabled={busy}
            className="gap-1.5 h-8 text-xs" data-testid="client-push-enable">
            <Bell className="w-3.5 h-3.5" /> {busy ? "Activation…" : "Activer les notifications"}
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => setShowPinHelp(true)}
          className="gap-1.5 h-8 text-xs text-neutral-500" data-testid="client-pin-help">
          <Share className="w-3.5 h-3.5" /> Épingler sur mon iPhone
        </Button>
      </div>

      {config.subscribed && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2"
          data-testid="client-push-prefs">
          <span className="flex items-center gap-1 font-medium text-neutral-800">
            <Bell className="w-3.5 h-3.5" /> Notifications actives
          </span>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={!!config.prefs.publications}
              onChange={(e) => setPref({ publications: e.target.checked })}
              className="accent-neutral-900" data-testid="client-pref-publications" />
            Publications à valider
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={!!config.prefs.contenus}
              onChange={(e) => setPref({ contenus: e.target.checked })}
              className="accent-neutral-900" data-testid="client-pref-contenus" />
            Demandes & contenus
          </label>
          <button onClick={unsubscribe}
            className="flex items-center gap-1 text-neutral-400 hover:text-red-600 transition-colors"
            data-testid="client-push-disable">
            <BellOff className="w-3.5 h-3.5" /> Désactiver
          </button>
        </div>
      )}

      <Dialog open={showPinHelp} onOpenChange={setShowPinHelp}>
        <DialogContent className="max-w-sm" data-testid="pin-help-modal" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading">Épingler sur votre écran d'accueil</DialogTitle>
          </DialogHeader>
          <ol className="text-sm text-neutral-700 space-y-2.5 list-decimal list-inside">
            <li>Ouvrez cette page dans <strong>Safari</strong> (obligatoire sur iPhone).</li>
            <li>Touchez le bouton <strong>Partager</strong> <Share className="w-3.5 h-3.5 inline -mt-0.5" /> (carré avec une flèche vers le haut, en bas de l'écran).</li>
            <li>Faites défiler puis touchez <strong>« Sur l'écran d'accueil »</strong>.</li>
            <li>Touchez <strong>« Ajouter »</strong>.</li>
          </ol>
          <p className="text-xs text-neutral-500 mt-1">
            L'icône apparaît sur votre écran d'accueil comme une application. Ouvrez-la depuis cette icône
            pour pouvoir activer les notifications (iOS 16.4 ou plus récent requis pour les notifications).
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
